import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { fetchHomepage } from "../api/marketingApi";
import { ListingCard } from "../components/ListingCard";
import { RooftopMark } from "../components/RooftopMark";
import { trackContactWhatsApp, trackContactPhone } from "../lib/metaPixel";

// Inline brand glyphs (self-contained — no external asset request). They inherit
// the button's text color via `currentColor`.
function WhatsAppIcon({ className = "h-[1.05em] w-[1.05em]" }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  );
}

function InstagramIcon({ className = "h-[1.05em] w-[1.05em]" }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069ZM12 0C8.741 0 8.332.014 7.052.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.332 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0Zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324ZM12 16a4 4 0 110-8 4 4 0 010 8Zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881Z" />
    </svg>
  );
}

function FacebookIcon({ className = "h-[1.05em] w-[1.05em]" }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073Z" />
    </svg>
  );
}

function YouTubeIcon({ className = "h-[1.05em] w-[1.05em]" }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  );
}

const OFFER_CARDS = [
  { state: "new", title: "New Properties", blurb: "Fresh launches and ready-to-move homes." },
  { state: "resale", title: "Resale", blurb: "Pre-owned homes and spaces, priced to move." },
  { state: "rental", title: "Rentals", blurb: "Furnished and unfurnished, ready to let." },
];

// Scroll-triggered reveal for below-the-fold sections.
const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.5, ease: [0.2, 0.8, 0.2, 1] },
};


export function MarketingHome() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    fetchHomepage()
      .then((d) => alive && setData(d))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  if (loading) {
    return <div className="mx-auto max-w-[74rem] px-6 py-24 font-sans text-[#78746A]">Loading…</div>;
  }
  if (!data) return null;

  const on = (key) => data.sections?.[key];
  const hero = data.hero || {};

  return (
    <>
      {/* ── Hero ─────────────────────────────────────────────── */}
      {on("hero") && (
        <section className="relative overflow-hidden">
          {hero.video_url ? (
            <video className="absolute inset-0 h-full w-full object-cover opacity-[0.12]" src={hero.video_url} autoPlay muted loop playsInline />
          ) : hero.image_url ? (
            <img className="absolute inset-0 h-full w-full object-cover opacity-[0.12]" src={hero.image_url} alt="" />
          ) : null}
          <div className="relative mx-auto grid max-w-[76rem] items-center gap-10 px-6 pb-12 pt-14 lg:grid-cols-[1.05fr_0.95fr]">
            {/* Text column */}
            <div>
              <span className="font-sans text-[0.72rem] font-bold uppercase tracking-[0.2em] text-[#B7975A]">
                {hero.eyebrow}
              </span>
              <h1 className="mb-5 mt-3 max-w-[15ch] text-balance text-[clamp(2.6rem,6vw,4.2rem)] font-bold leading-[1.02] tracking-[-0.02em]">
                {hero.headline}
              </h1>
              <p className="max-w-[40rem] text-[1.2rem] leading-relaxed text-[#4A473E]">
                {hero.subcopy}
              </p>
              <div className="mt-8 flex flex-wrap gap-3 font-sans">
                {hero.primary_cta && (
                  <Link to={hero.primary_cta.href || "/properties"} className="inline-flex items-center gap-2 rounded-[10px] bg-[#2C4A87] px-[1.4rem] py-[0.85rem] text-[0.98rem] font-semibold text-white shadow-[0_4px_14px_rgba(44,74,135,0.25)] transition hover:bg-[#1F3766]">
                    {hero.primary_cta.label} <ArrowRight className="h-4 w-4" />
                  </Link>
                )}
                {hero.secondary_cta && (
                  <Link to={hero.secondary_cta.href || "/about"} className="inline-flex items-center gap-2 rounded-[10px] border border-[#C9C3B4] px-[1.4rem] py-[0.85rem] text-[0.98rem] font-semibold hover:border-[#78746A]">
                    {hero.secondary_cta.label}
                  </Link>
                )}
              </div>

              {on("stats") && data.stats?.listings > 0 && (
                <div className="mt-10 flex gap-10 font-sans text-[#4A473E]">
                  <Stat value={data.stats.listings} label="Live listings" />
                  {data.stats.cities > 0 && <Stat value={data.stats.cities} label="Cities" />}
                </div>
              )}
            </div>

            {/* Image column — configurable side image, with a graceful fallback. */}
            <div className="relative hidden lg:block">
              <div className="absolute -right-6 -top-6 h-40 w-40 rounded-full bg-[#B7975A]/15 blur-2xl" />
              <div className="absolute -bottom-8 -left-4 h-48 w-48 rounded-full bg-[#2C4A87]/10 blur-2xl" />
              {hero.side_image_url ? (
                <img
                  src={hero.side_image_url}
                  alt=""
                  className="relative aspect-[4/5] w-full rounded-[24px] object-cover shadow-[0_20px_50px_rgba(20,19,15,0.15)]"
                />
              ) : (
                <div className="relative flex aspect-[4/5] w-full items-center justify-center rounded-[24px] bg-gradient-to-br from-[#2C4A87] to-[#1F3766] shadow-[0_20px_50px_rgba(20,19,15,0.15)]">
                  <RooftopMark size={120} navy="#ffffff" gold="#CBB07B" />
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ── Services ─────────────────────────────────────────── */}
      {on("services") && data.services?.items?.length > 0 && (
        <Section eyebrow={data.services.eyebrow} title={data.services.title}>
          <div className="grid gap-6 [grid-template-columns:repeat(auto-fit,minmax(230px,1fr))]">
            {data.services.items.map((s, i) => (
              <motion.div
                {...reveal}
                key={i}
                className="group flex flex-col overflow-hidden rounded-2xl border border-[#DED9CC] bg-[#FBFAF6] transition hover:-translate-y-1 hover:shadow-[0_14px_34px_rgba(20,19,15,0.08)]"
              >
                {s.image_url ? (
                  <img src={s.image_url} alt="" className="aspect-[16/10] w-full object-cover" loading="lazy" />
                ) : (
                  <div className="flex aspect-[16/10] w-full items-center justify-center bg-gradient-to-br from-[#EDEAE2] to-[#E2DDCF]">
                    <RooftopMark size={44} navy="#2C4A87" gold="#B7975A" />
                  </div>
                )}
                <div className="flex flex-1 flex-col p-5">
                  <h3 className="m-0 text-[1.15rem] font-bold leading-tight">{s.title}</h3>
                  {s.body && <p className="mt-2 font-sans text-[0.9rem] leading-relaxed text-[#4A473E]">{s.body}</p>}
                </div>
              </motion.div>
            ))}
          </div>
        </Section>
      )}

      {/* ── Offer cards ──────────────────────────────────────── */}
      {on("offer_cards") && (
        <section className="mx-auto max-w-[74rem] px-6 pb-8 pt-4">
          <div className="grid gap-6 [grid-template-columns:repeat(auto-fit,minmax(260px,1fr))]">
            {OFFER_CARDS.map((c) => {
              const img = data.offer_images?.[c.state];
              return (
                <Link
                  key={c.state}
                  to={`/properties?state=${c.state}`}
                  className="group relative flex min-h-[220px] flex-col justify-end overflow-hidden rounded-2xl border border-[#DED9CC] shadow-[0_1px_2px_rgba(20,19,15,0.04)] transition hover:-translate-y-1 hover:shadow-[0_16px_36px_rgba(20,19,15,0.14)]"
                >
                  {img ? (
                    <>
                      <img src={img} alt="" className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-105" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
                    </>
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-[#EDEAE2] to-[#E2DDCF]" />
                  )}
                  <div className="relative p-[1.5rem]">
                    <span className={`font-sans text-[0.7rem] font-bold uppercase tracking-[0.12em] ${img ? "text-[#E9D9B4]" : "text-[#B7975A]"}`}>{c.title}</span>
                    <div className={`mt-1.5 font-sans text-[0.95rem] font-medium ${img ? "text-white" : "text-[#4A473E]"}`}>{c.blurb}</div>
                    <div className={`mt-3 inline-flex items-center gap-1 font-sans text-[0.82rem] font-semibold ${img ? "text-white" : "text-[#2C4A87]"}`}>
                      Explore <ArrowRight className="h-3.5 w-3.5" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Featured ─────────────────────────────────────────── */}
      {on("featured") && data.featured?.length > 0 && (
        <Section eyebrow="Featured" title="Latest properties" sub="A look at what's just come onto our books.">
          <Grid listings={data.featured} />
          <div className="mt-10 text-center font-sans">
            <Link to="/properties" className="inline-flex items-center gap-2 rounded-[10px] border border-[#C9C3B4] px-[1.3rem] py-[0.8rem] font-semibold hover:border-[#78746A]">
              View all properties <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Section>
      )}

      {/* ── About ────────────────────────────────────────────── */}
      {on("about") && data.about && (
        <section className="bg-[#EDEAE2]">
          <div className="mx-auto max-w-[52rem] px-6 py-16">
            <SectionHeading eyebrow={data.about.eyebrow} title={data.about.title} />
            <div className="prose prose-lg max-w-none text-[#4A473E]" dangerouslySetInnerHTML={{ __html: data.about.body_html || "" }} />
            {data.about.highlights?.length > 0 && (
              <div className="mt-9 grid gap-6 [grid-template-columns:repeat(auto-fit,minmax(180px,1fr))]">
                {data.about.highlights.map((h, i) => (
                  <div key={i} className="border-l-2 border-[#B7975A] pl-4">
                    <h3 className="m-0 text-[1.2rem] font-bold">{h.title}</h3>
                    <p className="mt-1 font-sans text-[0.92rem] leading-relaxed text-[#4A473E]">{h.body}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── Portfolio ────────────────────────────────────────── */}
      {on("portfolio") && data.portfolio?.length > 0 && (
        <Section eyebrow="Previous work" title="Homes we've handed over" sub="A selection of properties we've helped our clients into.">
          <Grid listings={data.portfolio} />
        </Section>
      )}

      {/* ── Testimonials ─────────────────────────────────────── */}
      {on("testimonials") && data.testimonials?.items?.length > 0 && (
        <Section eyebrow={data.testimonials.eyebrow || "Testimonials"} title={data.testimonials.title || "What our clients say"}>
          <div className="grid gap-6 [grid-template-columns:repeat(auto-fit,minmax(300px,1fr))]">
            {data.testimonials.items.map((t, i) => (
              <motion.blockquote {...reveal} key={i} className="flex flex-col rounded-2xl border border-[#DED9CC] bg-[#FBFAF6] p-7 shadow-[0_1px_2px_rgba(20,19,15,0.03)]">
                <span className="font-serif text-[3rem] leading-[0.5] text-[#B7975A]/60">“</span>
                <p className="mt-3 flex-1 text-[1.05rem] leading-relaxed text-[#14130F]">{t.quote}</p>
                <footer className="mt-5 flex items-center gap-3">
                  {t.avatar_url ? (
                    <img src={t.avatar_url} alt="" className="h-11 w-11 rounded-full object-cover" loading="lazy" />
                  ) : (
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#2C4A87] font-sans text-[0.95rem] font-bold text-white">
                      {(t.name || "?").charAt(0).toUpperCase()}
                    </span>
                  )}
                  <div className="font-sans">
                    <div className="text-[0.92rem] font-bold text-[#14130F]">{t.name}</div>
                    {t.detail && <div className="text-[0.8rem] text-[#78746A]">{t.detail}</div>}
                  </div>
                </footer>
              </motion.blockquote>
            ))}
          </div>
        </Section>
      )}

      {/* ── Contact ──────────────────────────────────────────── */}
      {on("contact") && data.contact && <ContactSection contact={data.contact} />}
    </>
  );
}

function ContactSection({ contact }) {
  const waNumber = (contact.whatsapp || "").replace(/[^\d]/g, "");
  const waHref = waNumber ? `https://wa.me/${waNumber}` : null;
  // A QR that opens WhatsApp chat (or the site) — rendered via a QR image
  // service so no dependency is needed. Falls back to the site URL.
  const qrTarget = waHref || (typeof window !== "undefined" ? window.location.origin : "");
  const qrSrc = qrTarget
    ? `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=0&data=${encodeURIComponent(qrTarget)}`
    : null;

  return (
    <section id="contact" className="bg-[#EDEAE2]">
      <div className="mx-auto max-w-[74rem] px-6 py-16">
        <SectionHeading eyebrow="Contact" title={contact.title || "Get in touch"} sub={contact.subcopy} />

        <div className="grid gap-8 lg:grid-cols-[1.2fr_1fr]">
          {/* Details */}
          <div className="space-y-4 font-sans">
            {contact.address && (
              <ContactRow label="Visit us" value={contact.address} />
            )}
            {contact.hours && <ContactRow label="Hours" value={contact.hours} />}
            <div className="flex flex-wrap gap-x-10 gap-y-4">
              {contact.phone && (
                <ContactRow label="Call" value={<a href={`tel:${contact.phone}`} onClick={() => trackContactPhone()} className="text-[#2C4A87] hover:underline">{contact.phone}</a>} />
              )}
              {contact.email && (
                <ContactRow label="Email" value={<a href={`mailto:${contact.email}`} className="text-[#2C4A87] hover:underline">{contact.email}</a>} />
              )}
            </div>

            {/* Primary CTAs */}
            <div className="flex flex-wrap items-center gap-3 pt-2">
              {waHref && (
                <a
                  href={waHref}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackContactWhatsApp()}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-[10px] px-4 py-2.5 font-semibold text-white transition-opacity hover:opacity-90"
                  style={{ backgroundColor: "#25D366" }}
                >
                  <WhatsAppIcon className="h-5 w-5 shrink-0" />
                  WhatsApp us
                </a>
              )}
              <Link to="/properties" className="inline-flex items-center whitespace-nowrap rounded-[10px] border border-[#C9C3B4] px-4 py-2.5 font-semibold hover:border-[#78746A]">
                Browse properties
              </Link>
            </div>

            {/* Follow-us social icons (compact, so the section isn't crowded) */}
            {(contact.instagram_url || contact.facebook_url || contact.youtube_url) && (
              <div className="flex flex-wrap items-center gap-2.5 pt-1">
                <span className="mr-1 text-[0.82rem] text-[#78746A]">Follow us</span>
                {contact.instagram_url && (
                  <a
                    href={contact.instagram_url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Instagram"
                    title="Instagram"
                    className="flex h-9 w-9 items-center justify-center rounded-full text-white transition-opacity hover:opacity-90"
                    style={{ background: "linear-gradient(45deg, #F58529 0%, #DD2A7B 45%, #8134AF 75%, #515BD4 100%)" }}
                  >
                    <InstagramIcon className="h-[1.15rem] w-[1.15rem]" />
                  </a>
                )}
                {contact.facebook_url && (
                  <a
                    href={contact.facebook_url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Facebook"
                    title="Facebook"
                    className="flex h-9 w-9 items-center justify-center rounded-full text-white transition-opacity hover:opacity-90"
                    style={{ backgroundColor: "#1877F2" }}
                  >
                    <FacebookIcon className="h-[1.15rem] w-[1.15rem]" />
                  </a>
                )}
                {contact.youtube_url && (
                  <a
                    href={contact.youtube_url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="YouTube"
                    title="YouTube"
                    className="flex h-9 w-9 items-center justify-center rounded-full text-white transition-opacity hover:opacity-90"
                    style={{ backgroundColor: "#FF0000" }}
                  >
                    <YouTubeIcon className="h-[0.95rem] w-[0.95rem]" />
                  </a>
                )}
              </div>
            )}

            {qrSrc && (
              <div className="flex items-center gap-3 pt-4">
                <img src={qrSrc} alt="Scan to contact on WhatsApp" width={90} height={90} className="rounded-lg border border-[#DED9CC] bg-white p-1" loading="lazy" />
                <span className="text-[0.85rem] text-[#78746A]">Scan to chat with us on WhatsApp.</span>
              </div>
            )}
          </div>

          {/* Map */}
          {contact.map_embed_src && (
            <iframe
              title="Office location"
              src={contact.map_embed_src}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="h-[280px] w-full rounded-2xl border border-[#DED9CC] lg:h-full"
            />
          )}
        </div>
      </div>
    </section>
  );
}

function ContactRow({ label, value }) {
  return (
    <div>
      <div className="text-[0.7rem] uppercase tracking-[0.1em] text-[#78746A]">{label}</div>
      <div className="mt-0.5 text-[0.98rem] text-[#14130F]">{value}</div>
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <span className="text-[1.8rem] font-bold text-[#14130F]">{value}</span>
      <br />
      <span className="text-[0.82rem] uppercase tracking-[0.1em] text-[#78746A]">{label}</span>
    </div>
  );
}

export function SectionHeading({ eyebrow, title, sub }) {
  return (
    <motion.div {...reveal} className="mb-9">
      {eyebrow && (
        <div className="mb-3 flex items-center gap-3">
          <span className="h-px w-8 bg-[#B7975A]" />
          <span className="font-sans text-[0.72rem] font-bold uppercase tracking-[0.2em] text-[#B7975A]">{eyebrow}</span>
        </div>
      )}
      <h2 className="text-[clamp(1.9rem,4vw,2.8rem)] font-bold leading-[1.08] tracking-[-0.02em] text-[#14130F]">{title}</h2>
      {sub && <p className="mt-3 max-w-[44rem] text-[1.1rem] leading-relaxed text-[#4A473E]">{sub}</p>}
    </motion.div>
  );
}

function Section({ eyebrow, title, sub, children }) {
  return (
    <section className="mx-auto max-w-[74rem] px-6 py-16">
      <SectionHeading eyebrow={eyebrow} title={title} sub={sub} />
      {children}
    </section>
  );
}

function Grid({ listings }) {
  return (
    <div className="grid gap-6 [grid-template-columns:repeat(auto-fill,minmax(280px,1fr))]">
      {listings.map((l) => (
        <ListingCard key={l.token} listing={l} />
      ))}
    </div>
  );
}
